// KeyMetrics.js

import React, { useState, useEffect } from "react"; // ✅ Add useEffect

import { FiMaximize2, FiMoreHorizontal, FiX } from "react-icons/fi"; // Icons for Fullscreen and Close
import "./keymetrics.css"; // Metric card styles
import "./fullscreenHandler.css";
import WordCloud from "react-wordcloud";
import ApexCharts from "react-apexcharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faInstagram,
  faTiktok,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";

const KeyMetrics = ({
  searchTerm,
  fromDate,
  toDate,
  selectedSentiment,
  selectedMediaTypes,
  apiData,
}) => {
  const [expandedContent, setExpandedContent] = useState(
    () => localStorage.getItem("expandedContent") || null
  );
  const [selectedPlatform, setSelectedPlatform] = useState(
    () => localStorage.getItem("selectedPlatform") || "Facebook"
  );

  useEffect(() => {
    if (expandedContent) {
      localStorage.setItem("expandedContent", expandedContent);
    } else {
      localStorage.removeItem("expandedContent");
    }

    localStorage.setItem("selectedPlatform", selectedPlatform);
  }, [expandedContent, selectedPlatform]);

  useEffect(() => {
    const storedExpandedContent = localStorage.getItem("expandedContent");
    if (storedExpandedContent) {
      setExpandedContent(storedExpandedContent);
    }

    const storedSelectedPlatform = localStorage.getItem("selectedPlatform");
    if (storedSelectedPlatform) {
      setSelectedPlatform(storedSelectedPlatform);
    }
  }, []);

  // Function to open the expanded view
  const openExpandedView = (content) => {
    setExpandedContent(content); // Local storage update is handled by useEffect
  };

  const getPlatformEngagement = (platform) => {
    const totalLikes = getMetric(platform, "total_likes");
    const totalShares = getMetric(platform, "total_shares");
    const totalComments = getMetric(platform, "total_comments");
    const totalRetweets = getMetric(platform, "total_retweets");
    const totalQuotes = getMetric(platform, "total_quotes");
    const totalReplies = getMetric(platform, "total_replies");
    const totalViews = getMetric(platform, "total_views");
    const totalVideoViews = getMetric(platform, "total_video_views");
    const totalVideoPlays = getMetric(platform, "total_video_plays");

    if (platform === "Facebook")
      return totalLikes + totalShares + totalVideoViews;
    if (platform === "Instagram") return totalComments + totalVideoViews;
    if (platform === "TikTok")
      return totalLikes + totalShares + totalVideoPlays;
    if (platform === "Twitter")
      return (
        totalLikes + totalRetweets + totalQuotes + totalReplies + totalViews
      );

    return 0;
  };

  // Function to close the expanded view
  const closeExpandedView = () => {
    setExpandedContent(null); // Local storage update is handled by useEffect
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M"; // Convert to millions
    if (num >= 1000) return (num / 1000).toFixed(1) + "K"; // Convert to thousands
    return num.toString(); // Return as is if <1000
  };

  const getMetric = (platform, key) =>
    apiData && apiData.stats && apiData.stats[platform]
      ? apiData.stats[platform][key] || 0
      : 0;

  // Calculate the total number of posts across all selected platforms
  const totalPosts = selectedMediaTypes.reduce((acc, platform) => {
    return acc + getMetric(platform, "total_posts"); // Sum total posts for each platform
  }, 0);

  // Format the totalPosts (e.g., 1000 => 1K)
  const formattedTotalPosts =
    totalPosts >= 1000 ? (totalPosts / 1000).toFixed(1) + "K" : totalPosts;

  // Calculate the total engagement across all selected platforms
  const totalEngagementOverall = selectedMediaTypes.reduce((acc, platform) => {
    const totalLikes = getMetric(platform, "total_likes");
    const totalShares = getMetric(platform, "total_shares");
    const totalComments = getMetric(platform, "total_comments");
    const totalRetweets = getMetric(platform, "total_retweets");
    const totalQuotes = getMetric(platform, "total_quotes");
    const totalReplies = getMetric(platform, "total_replies");
    const totalViews = getMetric(platform, "total_views");
    const totalVideoViews = getMetric(platform, "total_video_views");
    const totalVideoPlays = getMetric(platform, "total_video_plays");

    let totalEngagement = 0;

    if (platform === "Facebook") {
      totalEngagement = totalLikes + totalShares + totalVideoViews;
    } else if (platform === "Instagram") {
      totalEngagement = totalComments + totalVideoViews;
    } else if (platform === "TikTok") {
      totalEngagement = totalLikes + totalShares + totalVideoPlays;
    } else if (platform === "Twitter") {
      totalEngagement =
        totalLikes + totalRetweets + totalQuotes + totalReplies + totalViews;
    }

    // Add the platform's total engagement to the accumulator
    return acc + totalEngagement;
  }, 0);

  // Format the total engagement across all platforms with "K" for thousands
  const formattedTotalEngagement =
    totalEngagementOverall >= 1000
      ? (totalEngagementOverall / 1000).toFixed(1) + "K"
      : totalEngagementOverall;

  const getPlatformReach = (platform) => {
    switch (platform) {
      case "Facebook":
        return getMetric(platform, "total_video_views"); // Reach for Facebook is based on total_video_views
      case "Instagram":
        return getMetric(platform, "total_video_views"); // Reach for Instagram is based on total_video_views
      case "TikTok":
        return getMetric(platform, "total_plays"); // Reach for TikTok is based on total_plays
      case "Twitter":
        return getMetric(platform, "total_views"); // Reach for Twitter is based on total_views
      default:
        return 0; // Default return 0 if platform is not recognized
    }
  };

  // Calculate the total potential reach across all selected platforms
  const totalReach = selectedMediaTypes.reduce((acc, platform) => {
    return acc + getPlatformReach(platform); // Add the reach for each platform
  }, 0);

  // Format the total reach with "K" for thousands
  const formattedTotalReach =
    totalReach >= 1000 ? (totalReach / 1000).toFixed(1) + "K" : totalReach;

  const renderExpandedContent = (content) => {
    switch (content) {
      case "results-card":
        return (
          <div className="fullscreen-value">
            {selectedMediaTypes.map((platform) => (
              <div key={platform} className="platform-card">
                <div className="keymetrics-icon-container">
                  {/* Render platform icons */}
                  {platform === "Facebook" && (
                    <FontAwesomeIcon
                      icon={faFacebook}
                      className="platform-icon"
                    />
                  )}
                  {platform === "Instagram" && (
                    <FontAwesomeIcon
                      icon={faInstagram}
                      className="platform-icon"
                    />
                  )}
                  {platform === "TikTok" && (
                    <FontAwesomeIcon
                      icon={faTiktok}
                      className="platform-icon"
                    />
                  )}
                  {platform === "Twitter" && (
                    <FontAwesomeIcon
                      icon={faTwitter}
                      className="platform-icon"
                    />
                  )}
                </div>
                {/* Render stats based on platform */}
                <div className="platform-stats">
                  <h2>
                    {getMetric(platform, "total_posts")} <br />
                  </h2>
                </div>
              </div>
            ))}
          </div>
        );

      case "engagement-card":
        return (
          <div className="fullscreen-value">
            {selectedMediaTypes.map((platform) => {
              const totalPlays = getMetric(platform, "total_plays");
              const totalreactions = getMetric(platform, "total_reactions");
              const totalLikes = getMetric(platform, "total_likes");
              const totalShares = getMetric(platform, "total_shares");
              const totalComments = getMetric(platform, "total_comments");
              const totalRetweets = getMetric(platform, "total_retweets");
              const totalQuotes = getMetric(platform, "total_quotes");
              const totalReplies = getMetric(platform, "total_replies");
              const totalViews = getMetric(platform, "total_views");
              const totalVideoViews = getMetric(platform, "total_video_views");
              const totalVideoPlays = getMetric(platform, "total_video_plays");

              // Calculating total engagement based on the available metrics
              let totalEngagement = 0;

              if (platform === "Facebook") {
                totalEngagement =
                  totalComments +
                  totalreactions +
                  totalShares +
                  totalVideoViews;
              } else if (platform === "Instagram") {
                totalEngagement =
                  totalComments + totalVideoViews + totalVideoPlays; // Assuming we use comments and video views for Instagram
              } else if (platform === "TikTok") {
                totalEngagement = totalLikes + totalShares + totalPlays;
              } else if (platform === "Twitter") {
                totalEngagement =
                  totalLikes +
                  totalRetweets +
                  totalQuotes +
                  totalReplies +
                  totalViews;
              }

              // Format total engagement with "K" for thousands
              const formattedEngagement =
                totalEngagement >= 1000
                  ? (totalEngagement / 1000).toFixed(1) + "K"
                  : totalEngagement;

              return (
                <div key={platform} className="platform-card">
                  <div className="keymetrics-icon-container">
                    {/* Render platform icons */}
                    {platform === "Facebook" && (
                      <FontAwesomeIcon
                        icon={faFacebook}
                        className="platform-icon"
                      />
                    )}
                    {platform === "Instagram" && (
                      <FontAwesomeIcon
                        icon={faInstagram}
                        className="platform-icon"
                      />
                    )}
                    {platform === "TikTok" && (
                      <FontAwesomeIcon
                        icon={faTiktok}
                        className="platform-icon"
                      />
                    )}
                    {platform === "Twitter" && (
                      <FontAwesomeIcon
                        icon={faTwitter}
                        className="platform-icon"
                      />
                    )}
                  </div>
                  <div className="platform-engagement">
                    <h2>{formattedEngagement}</h2>
                  </div>
                </div>
              );
            })}
          </div>
        );
      case "reach-card":
        return (
          <div className="fullscreen-value">
            {(() => {
              let totalReachSum = 0; // Initialize total reach sum

              const platformCards = selectedMediaTypes.map((platform) => {
                const totalReach = getPlatformReach(platform); // Get reach per platform
                totalReachSum += totalReach; // Accumulate total reach

                const formattedReach =
                  totalReach >= 1000
                    ? (totalReach / 1000).toFixed(1) + "K"
                    : totalReach; // Format reach

                return (
                  <div key={platform} className="platform-card">
                    <div className="keymetrics-icon-container">
                      {platform === "Facebook" && (
                        <FontAwesomeIcon
                          icon={faFacebook}
                          className="platform-icon"
                        />
                      )}
                      {platform === "Instagram" && (
                        <FontAwesomeIcon
                          icon={faInstagram}
                          className="platform-icon"
                        />
                      )}
                      {platform === "TikTok" && (
                        <FontAwesomeIcon
                          icon={faTiktok}
                          className="platform-icon"
                        />
                      )}
                      {platform === "Twitter" && (
                        <FontAwesomeIcon
                          icon={faTwitter}
                          className="platform-icon"
                        />
                      )}
                    </div>
                    <div className="platform-reach">
                      <h2>{formattedReach}</h2>
                    </div>
                  </div>
                );
              });

              const formattedTotalReach =
                totalReachSum >= 1000
                  ? (totalReachSum / 1000).toFixed(1) + "K"
                  : totalReachSum; // Format total reach

              return (
                <>
                  {platformCards}
                  {/* Display Total Reach */}
                  {/* <div className="total-reach">
                      <p>
                      {formattedTotalReach}
                      </p>
                    </div> */}
                </>
              );
            })()}
          </div>
        );

      case "resultgraph":
        return (
          <div className="fullscreen-value">
            <h3 className="metric-header-resultgraph">
              Graph for Results Over Time
            </h3>
            <ApexCharts
              options={{
                ...options,
                colors: resultsGraphData?.colors || ["#000"], // Ensure colors exist
                title: {
                  text: resultsGraphData.title,
                },
              }}
              series={resultsGraphData.series}
              type="line"
              height={500} // Fullscreen mode
            />
          </div>
        );

      case "world-cloud":
        return (
          <div className="fullscreen-value">
            {/* Platform Selection */}
            <div className="platform-selector">
              {["Twitter", "Facebook", "Instagram", "TikTok"].map(
                (platformName) => (
                  <label key={platformName} className="platform-label">
                    <input
                      type="radio"
                      name="platform"
                      value={platformName}
                      checked={selectedPlatform === platformName}
                      onChange={() => {
                        setSelectedPlatform(platformName);
                        localStorage.setItem("selectedPlatform", platformName); // Store in localStorage
                      }}
                    />
                    <span
                      className="platform-circle"
                      style={{
                        backgroundColor: getPlatformColor(platformName),
                      }}
                    ></span>
                    {platformName}
                  </label>
                )
              )}
            </div>

            {/* Word Cloud Display */}
            <div className="chart-container-content">
              {wordCloudData.length > 0 ? (
                <WordCloud words={wordCloudData} options={worldOptions} />
              ) : (
                <p>No data available for platforms</p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getDailyPostCounts = (platform) => {
    if (
      !apiData ||
      !apiData.stats ||
      !apiData.stats[platform]?.daily_post_counts
    ) {
      return {};
    }

    // Extract the daily post counts object
    const dailyCounts = apiData.stats[platform].daily_post_counts;

    // Sort the entries by date in ascending order before returning
    return Object.fromEntries(
      Object.entries(dailyCounts).sort(
        ([dateA], [dateB]) => new Date(dateA) - new Date(dateB)
      )
    );
  };

  const generateResultsGraphData = () => {
    if (!apiData || !apiData.stats) {
      return {
        series: [],
        title: "Performance Over Time",
        subtitle: "Tracking metrics across days",
        colors: [],
      };
    }

    const platformColors = {
      Facebook: "#1877F2",
      Instagram: "#E1306C",
      TikTok: "#69C9D0",
      Twitter: "#1DA1F2",
    };

    const series = selectedMediaTypes
      .map((platform) => {
        const dailyCounts = getDailyPostCounts(platform);

        if (Object.keys(dailyCounts).length === 0) return null; // Skip if no data

        return {
          name: platform,
          data: Object.entries(dailyCounts).map(([date, count]) => ({
            x: date,
            y: count,
          })),
        };
      })
      .filter(Boolean); // Remove null values if any platform doesn't have data

    return {
      series,
      title: "Post Results Over Time",
      subtitle: "Tracking metrics across days",
      colors: series.map((s) => platformColors[s.name] || "#000"),
    };
  };

  const resultsGraphData = generateResultsGraphData();

  const getPlatformColor = (platform) => {
    const colors = {
      Twitter: "#1DA1F2",
      Facebook: "#1877F2",
      Instagram: "#E1306C",
      TikTok: "#69C9D0",
    };
    return colors[platform] || "#999";
  };

  const getWordCloudData = (platform, apiData) => {
    return (
      apiData?.stats?.[platform]?.word_cloud?.map((word) => ({
        text: word.text,
        value: word.value,
      })) || []
    );
  };
  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "Facebook":
        return faFacebook;
      case "Instagram":
        return faInstagram;
      case "TikTok":
        return faTiktok;
      case "Twitter":
        return faTwitter;
      default:
        return null;
    }
  };

  const getWorldCloudOptions = () => {
    return {
      rotations: 2,
      rotationAngles: [-90, 0],
      fontSizes: [15, 60],
      color: "random-light",
      fontFamily: "monospace",
    };
  };

  // Function to handle platform selection logic (if needed)
  const handlePlatformSelection = (selectedPlatform, apiData) => {
    return getWordCloudData(selectedPlatform, apiData);
  };

  // Fetch word cloud data dynamically for the selected platform
  const wordCloudData = handlePlatformSelection(selectedPlatform, apiData);

  // Fetch word cloud options
  const worldOptions = getWorldCloudOptions();

  // Options for both charts
  const options = {
    chart: {
      type: "line",
      height: 350,
    },
    xaxis: {
      type: "datetime",
    },
    title: {
      text: "Graph Title",
      align: "left",
    },
    yaxis: {
      title: {
        text: "Values",
      },
    },
    tooltip: {
      x: {
        format: "dd MMM yyyy",
      },
    },
  };

  return (
    <div className="key-metrics-grid">
      {/* Left Section: Metric Cards */}
      <div className="metrics-section">
        <div id="results-card" className="metric-card">
          <div className="short-metric-card-header">
            <h3 className="metric-header">TOTAL POSTS</h3>

            <div className="card-icons">
              <FiMaximize2
                className="icon"
                onClick={() => openExpandedView("results-card")}
              />
              <FiMoreHorizontal className="icon" />
            </div>
          </div>

          <div className="separator"></div>

          {/* Multi-value grid layout for platform-wise Total Posts */}
          <div className="metric-multi-value">
            {selectedMediaTypes.map((platform) => (
              <div key={platform} className="platform-metric">
                <FontAwesomeIcon
                  icon={getPlatformIcon(platform)}
                  className="platform-icon"
                />
                <span>{getMetric(platform, "total_posts")}</span>
              </div>
            ))}
          </div>
        </div>

        <div id="engagement-card" className="metric-card">
          <div className="short-metric-card-header">
            <h3 className="metric-header">ENGAGEMENT</h3>
            <div className="card-icons">
              <FiMaximize2
                className="icon"
                onClick={() => openExpandedView("engagement-card")}
              />
              <FiMoreHorizontal className="icon" />
            </div>
          </div>

          <div className="separator"></div>

          {/* Multi-value grid layout for platform-wise Engagement */}
          <div className="metric-multi-value">
            {selectedMediaTypes.map((platform) => (
              <div key={platform} className="platform-metric">
                <FontAwesomeIcon
                  icon={getPlatformIcon(platform)}
                  className="platform-icon"
                />
                <span>{formatNumber(getPlatformEngagement(platform))}</span>
              </div>
            ))}
          </div>
        </div>

        {/* <div id="sentiment-card" className="metric-card">
          <div className="short-metric-card-header">
            <h3 className="metric-header">SENTIMENT</h3>
            <div className="card-icons">
              <FiMaximize2
                className="icon"
                onClick={() => openExpandedView("sentiment-card")}
              />
              <FiMoreHorizontal className="icon" />
            </div>
          </div>
          <div className="separator"></div>
          <div className="metric-value">
            <span>😊 25.6%</span>

            <span>😡 13.5%</span>
          </div>
        </div> */}

        <div id="reach-card" className="metric-card">
          <div className="short-metric-card-header">
            <h3 className="metric-header">TOTAL REACH</h3>
            <div className="card-icons">
              <FiMaximize2
                className="icon"
                onClick={() => openExpandedView("reach-card")}
              />
              <FiMoreHorizontal className="icon" />
            </div>
          </div>

          <div className="separator"></div>

          {/* Multi-value grid layout for platform-wise Reach */}
          <div className="metric-multi-value">
            {selectedMediaTypes.map((platform) => (
              <div key={platform} className="platform-metric">
                <FontAwesomeIcon
                  icon={getPlatformIcon(platform)}
                  className="platform-icon"
                />
                <span>{formatNumber(getPlatformReach(platform))}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="graphs-section">
        {/* Results Graph */}
        <div
          id="resultgraph"
          className="chart-container chart-container-resultgraph"
        >
          <div className="metric-card-header">
            <h3 className="metric-header-resultgraph">Results Graph</h3>
            <div className="card-icons-resultgraph">
              <FiMaximize2
                className="icon"
                onClick={() => openExpandedView("resultgraph")}
              />
              <FiMoreHorizontal className="icon" />
            </div>
          </div>

          {/* Wrapped ApexCharts in a div */}
          <div className="graph-container">
            <ApexCharts
              options={{
                ...options,
                colors: resultsGraphData?.colors || ["#000"], // Fallback color
                title: { text: resultsGraphData.title },
              }}
              series={resultsGraphData.series}
              type="line"
              height={350}
            />
          </div>
        </div>

        <div
          id="world-cloud"
          className="chart-container chart-container-netsentiment"
        >
          {/* Card Header */}
          <div className="metric-card-header">
            <h3 className="metric-header-sentiment">
              Net Sentiment Word Cloud
            </h3>
            <div className="card-icons-sentiment">
              <FiMaximize2
                className="icon"
                onClick={() =>
                  console.log("Open expanded view for netsentimentgraph")
                }
              />
              <FiMoreHorizontal className="icon" />
            </div>
          </div>

          {/* Word Cloud Display */}
          <div className="chart-container-content">
            {wordCloudData.length > 0 ? (
              <WordCloud words={wordCloudData} options={worldOptions} />
            ) : (
              <p>No data available for platforms</p>
            )}
          </div>

          {/* Platform Selection (Radio Buttons) */}
          <div className="platform-selector">
            {["Twitter", "Facebook", "Instagram", "TikTok"].map(
              (platformName) => (
                <label key={platformName} className="platform-label">
                  <input
                    type="radio"
                    name="platform"
                    value={platformName}
                    checked={selectedPlatform === platformName}
                    onChange={() => {
                      setSelectedPlatform(platformName);
                      localStorage.setItem("selectedPlatform", platformName); // Store in localStorage
                    }}
                  />
                  <span
                    className="platform-circle"
                    style={{ backgroundColor: getPlatformColor(platformName) }}
                  ></span>
                  {platformName}
                </label>
              )
            )}
          </div>
        </div>
      </div>
      {/* Expanded View Modal */}
      {expandedContent && (
        <div className="expanded-modal">
          <div className="modal-content">
            <div className="metric-card-header">
              <h3 className="metric-header">
                {expandedContent.replace(/-/g, " ").toUpperCase()}
              </h3>
              <FiX className="icon close-icon" onClick={closeExpandedView} />
            </div>
            <div className="fullscreen-separator"></div>
            {renderExpandedContent(expandedContent)}
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyMetrics;
